import React, { useMemo, useState } from 'react';
import {
  CheckCircle,
  Users,
  Code,
  Database,
  Layout,
  Award,
  Shield,
  Clock,
  HelpCircle,
  Zap,
  Headphones,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useLanguage } from '../i18n/LanguageContext';

const stepIcons = [
  <Users className="w-12 h-12 text-purple-600" />,
  <Layout className="w-12 h-12 text-purple-600" />,
  <Code className="w-12 h-12 text-purple-600" />,
  <Database className="w-12 h-12 text-purple-600" />,
  <CheckCircle className="w-12 h-12 text-purple-600" />,
  <Award className="w-12 h-12 text-purple-600" />,
];

const scenarioIcons = [
  <HelpCircle className="w-12 h-12 text-purple-600" />,
  <Zap className="w-12 h-12 text-purple-600" />,
  <Database className="w-12 h-12 text-purple-600" />,
  <Shield className="w-12 h-12 text-purple-600" />,
];

const paymentIcons = [
  <Shield className="w-12 h-12 mx-auto mb-4 text-purple-600" />,
  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-purple-600" />,
  <Award className="w-12 h-12 mx-auto mb-4 text-purple-600" />,
];

const WorkFlow = () => {
  const [activeTab, setActiveTab] = useState(0);
  const { dict } = useLanguage();
  const w = dict.workflow;

  const serviceAllocationData = useMemo(
    () => [
      { name: w.chartDev, value: 33.33, color: '#9370db' },
      { name: w.chartInfra, value: 33.33, color: '#6a47bf' },
      { name: w.chartDesign, value: 33.34, color: '#d7c0ff' },
    ],
    [w.chartDev, w.chartInfra, w.chartDesign]
  );

  const costComparisonData = useMemo(
    () => [
      {
        name: w.chartTraditional,
        development: 40,
        infrastructure: 35,
        design: 25,
        total: 100,
      },
      {
        name: w.chartOurRate,
        development: 20,
        infrastructure: 20,
        design: 20,
        total: 60,
      },
    ],
    [w.chartTraditional, w.chartOurRate]
  );

  const tabTitles = [w.tabAllocation, w.tabCost];

  const tabContents = [
    <div className="p-6" key="allocation">
      <h4 className="text-xl font-bold mb-4 text-center">{w.allocationTitle}</h4>
      <p className="text-gray-600 mb-6 text-center">{w.allocationSubtitle}</p>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={serviceAllocationData}
              cx="50%"
              cy="50%"
              labelLine={true}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
            >
              {serviceAllocationData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-center mt-4 flex-wrap">
        {serviceAllocationData.map((entry, index) => (
          <div key={index} className="flex items-center mx-3">
            <div className="w-4 h-4 mr-2" style={{ backgroundColor: entry.color }}></div>
            <span className="text-sm">{entry.name}</span>
          </div>
        ))}
      </div>
    </div>,
    <div className="p-6" key="cost">
      <h4 className="text-xl font-bold mb-4 text-center">{w.costTitle}</h4>
      <p className="text-gray-600 mb-6 text-center">{w.costSubtitle}</p>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={costComparisonData}
            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="development" name={w.chartDev} stackId="a" fill="#9370db" />
            <Bar dataKey="infrastructure" name={w.chartInfra} stackId="a" fill="#6a47bf" />
            <Bar dataKey="design" name={w.chartDesign} stackId="a" fill="#d7c0ff" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="text-center mt-4 font-medium">
        <p>{w.costFooter}</p>
      </div>
    </div>,
  ];

  return (
    <section id="workflow" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold mb-4 text-black">{w.title}</h2>
          <p className="max-w-2xl mx-auto text-gray-600">{w.subtitle}</p>
        </div>

        <div className="bg-purple-100 rounded-lg p-6 mb-12 shadow-md">
          <div className="flex flex-col md:flex-row items-center justify-center">
            <div className="bg-white rounded-full p-4 md:mr-6 mb-4 md:mb-0">
              <Shield className="w-12 h-12 text-purple-600" />
            </div>
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-black mb-2">{w.guaranteeTitle}</h3>
              <p className="text-gray-700">
                <span className="font-semibold">{w.guaranteeLead}</span>{' '}
                {w.guaranteeRest}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gradient-to-r from-purple-500 to-purple-700 p-8 rounded-lg shadow-lg text-white mb-8">
            <div className="flex flex-col md:flex-row items-center">
              <div className="mb-6 md:mb-0 md:mr-8">
                <div className="bg-white p-4 rounded-full inline-block">
                  <Headphones className="w-16 h-16 text-purple-600" />
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-4">{w.onDemandTitle}</h3>
                <p className="text-lg mb-4 opacity-90">{w.onDemandBody}</p>
                <div className="flex flex-wrap gap-4">
                  {w.badges.map((badge) => (
                    <div
                      key={badge}
                      className="bg-white text-purple-600 px-4 py-2 rounded-full font-bold text-sm"
                    >
                      {badge}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {w.scenarios.map((scenario, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-start">
                  <div className="bg-purple-100 p-3 rounded-lg mr-4">{scenarioIcons[index]}</div>
                  <div>
                    <h4 className="text-xl font-bold mb-2 text-black">{scenario.title}</h4>
                    <p className="text-gray-600">{scenario.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg">
            <div className="flex flex-col lg:flex-row items-center">
              <div className="lg:w-1/2 mb-8 lg:mb-0 lg:pr-8">
                <h4 className="text-xl font-bold mb-4 text-black">{w.howTitle}</h4>
                <ul className="space-y-4">
                  {w.howSteps.map((step, index) => (
                    <li key={index} className="flex items-start">
                      <div className="bg-purple-100 rounded-full p-1 mr-3 mt-1">
                        <span className="flex items-center justify-center w-6 h-6 text-purple-600 font-bold">
                          {index + 1}
                        </span>
                      </div>
                      <p className="text-gray-600">
                        <span className="font-semibold text-black">{step.lead}</span>
                        {step.body}
                      </p>
                    </li>
                  ))}
                </ul>
                <div className="mt-6">
                  <a
                    href="#contact"
                    className="inline-block bg-purple-600 text-white font-bold py-3 px-6 rounded-lg shadow hover:bg-purple-700 transition duration-300"
                  >
                    {w.bookCta}
                  </a>
                </div>
              </div>
              <div className="lg:w-1/2 bg-purple-50 p-6 rounded-lg">
                <h4 className="text-xl font-bold mb-4 text-center text-black">{w.benefitsTitle}</h4>
                <div className="space-y-4">
                  {w.benefits.map((benefit) => (
                    <div key={benefit} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-purple-600 mr-3 flex-shrink-0" />
                      <p className="text-gray-700">{benefit}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-purple-100 rounded-lg">
                  <p className="font-bold text-purple-800 text-center">{w.quote}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-center mb-6 text-black">{w.advantageTitle}</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {w.pillars.map((pillar, index) => {
              const Icon = [Code, Database, Layout][index];
              const bg =
                index === 0
                  ? 'from-purple-50 to-purple-100'
                  : index === 1
                    ? 'from-purple-100 to-purple-200'
                    : 'from-purple-200 to-purple-300';
              return (
                <div
                  key={pillar.title}
                  className={`bg-gradient-to-br ${bg} p-6 rounded-lg text-center shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
                >
                  <div className="bg-white rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4 shadow-md">
                    <Icon className="w-10 h-10 text-purple-600" />
                  </div>
                  <h4 className="font-bold text-lg mb-2 text-black">{pillar.title}</h4>
                  <p className="text-gray-600">{pillar.description}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white p-6 rounded-lg shadow-lg text-center mb-8">
            <div className="flex flex-col md:flex-row items-center justify-center">
              <div className="bg-purple-100 rounded-full p-3 mb-4 md:mb-0 md:mr-6">
                <Clock className="w-8 h-8 text-purple-600" />
              </div>
              <div>
                <h4 className="text-xl font-bold mb-3">{w.rateTitle}</h4>
                <p className="text-gray-600 mb-4">{w.rateBody}</p>
              </div>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
              {w.rateBadges.map((badge) => (
                <div key={badge} className="bg-purple-50 p-3 rounded-lg">
                  <span className="font-bold text-purple-800">{badge}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="flex border-b">
              {tabTitles.map((title, index) => (
                <button
                  key={title}
                  className={`flex-1 py-4 px-6 text-center font-medium transition-colors duration-300 ${
                    activeTab === index
                      ? 'bg-purple-50 text-purple-600 border-b-2 border-purple-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  onClick={() => setActiveTab(index)}
                >
                  {title}
                </button>
              ))}
            </div>
            {tabContents[activeTab]}
          </div>
        </div>

        <div className="mb-16">
          <h3 className="text-2xl font-bold text-center mb-12 text-black">{w.processTitle}</h3>

          <div className="relative">
            <div className="absolute hidden md:block left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-200 via-purple-400 to-purple-600 transform -translate-x-1/2"></div>

            <div className="space-y-16">
              {w.steps.map((step, index) => (
                <div key={step.title} className="relative group">
                  <div
                    className={`flex flex-col md:flex-row items-center ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className="z-10 flex items-center justify-center w-16 h-16 bg-white rounded-full border-4 border-purple-400 mb-4 md:mb-0 shadow-lg transition-all duration-500 group-hover:scale-110 group-hover:border-purple-600">
                      <span className="text-xl font-bold text-purple-600">{index + 1}</span>
                    </div>

                    <div
                      className={`md:w-5/12 bg-white p-6 rounded-lg shadow-md transition-all duration-300 group-hover:shadow-xl ${
                        index % 2 === 0 ? 'md:ml-6' : 'md:mr-6'
                      } transform group-hover:-translate-y-1`}
                    >
                      <div className="flex items-center mb-3">
                        {stepIcons[index]}
                        <h3 className="text-xl font-bold ml-3 text-black">{step.title}</h3>
                      </div>
                      <p className="text-gray-600">{step.description}</p>

                      <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                        <div
                          className="bg-purple-600 h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${((index + 1) / w.steps.length) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-lg shadow-lg text-center mb-16">
          <h3 className="text-2xl font-bold mb-6 text-black">{w.paymentTitle}</h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {w.paymentItems.map((item, index) => (
              <div key={item.title} className="p-6 rounded-lg bg-purple-50">
                {paymentIcons[index]}
                <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-16 text-center bg-gradient-to-r from-purple-500 to-purple-700 p-10 rounded-lg shadow-lg text-white">
          <h3 className="text-2xl font-bold mb-4">{w.ctaTitle}</h3>
          <p className="max-w-2xl mx-auto mb-4 opacity-90">{w.ctaP1}</p>
          <p className="max-w-2xl mx-auto mb-8 opacity-90">{w.ctaP2}</p>
          <a
            href="#contact"
            className="inline-block bg-white text-purple-600 font-bold py-3 px-8 rounded-lg shadow-md hover:bg-purple-50 transition duration-300 transform hover:-translate-y-1 hover:shadow-lg"
          >
            {w.ctaButton}
          </a>
        </div>
      </div>
    </section>
  );
};

export default WorkFlow;
